import { DCEvent, httpDelete, httpGet, httpPost, HttpResponse } from "@/events/eventsDomain";
import { CustomDocumentType } from "../customDocument/customDocumentDomain";
import { DecisionType } from "@/common/decisions";
import { LocalizedText } from "@/common/common";
import { DocumentType } from "@/common/documents";
import {
  DocumentPreview,
  ProjectDecisionItemDTO,
  ProjectListItem,
  WithDecisionType,
} from "./commonDomain";
import { FileUploadConfig } from "@/fileUpload/fileUploadDomain";
import {
  DocumentSelection,
  MeetingForm,
  MeetingOption,
  MeetingType,
} from "@/meetings/meetingsDomain";
import { Language } from "@/i18n/languages";
import { ExportContent } from "@/shareholderRegister/shareholders/ExportShareholdersDomain";
import { ProjectTemplateAccess } from "@/dbot/projectList/projectListDomain.ts";

type UpToDate = {
  type: "upToDate";
};

export type UpdatedPreview = {
  preview: string;
  projectDocumentId: string;
  orgNo: string;
};

type AoAAIUpdate = {
  updatedPreviews: UpdatedPreview[];
};

type OutOfDateReason = AoAAIUpdate;

type OutOfDate = {
  type: "outOfDate";
  newVersion: number;
  reason: OutOfDateReason;
  pollStatus: boolean;
};

export type ProjectStatusResponse = UpToDate | OutOfDate;

export type CreateProjectDecisionReference = {
  projectId: string;
  decisionId: string;
};

export type SelectedDecision = {
  decisionType: DecisionType;
  decisionId?: string;
  titles?: LocalizedText[];
  decisionData?: WithDecisionType<DecisionType>;
};

type ProjectFromTemplate = {
  templateId: string;
  decisionIds: string[];
  documentIds: string[];
  attachmentIds: string[];
};

export interface CreateProjectRequest {
  orgNo: string;
  projectName: string;
  isGeneratedName?: boolean;
  decisions?: SelectedDecision[];
  documents?: DocumentType[];
  duplicateProjectId?: string;
  template?: ProjectFromTemplate;
  decisionReferences?: CreateProjectDecisionReference[];
  batchCompanies?: string[];
  customDocumentType?: CustomDocumentType;
  documentSource?: DocumentSource;
}

export type DocumentSource = {
  type: "shareholderRegister";
  format: ExportContent;
  language: string;
  date: string;
};

export type CreateProjectResponse = { projectId: string; documentId?: string };

export type GetProjectListResponse = {
  projects: ProjectListItem[];
  fileUploadConfig: FileUploadConfig;
  requiredDocumentsByDecision: { [decisionType: string]: DocumentType[] };
  templateAccess: ProjectTemplateAccess | undefined;
};

export type RecentMattersResponse = {
  items: ProjectDecisionItemDTO[];
};

export type RecentMatterDetailsResponse = {
  previews: DocumentPreview[];
};

export type CreateMeetingRequest = {
  orgNo: string;
  projectName: string;
  meetingType: MeetingType;
  meetingDocumentSelection?: DocumentSelection;
  meetingForm?: MeetingForm;
  meetingStartDate: string;
  meetingEndDate?: string;
  conductElectronicGeneralMeeting?: boolean;
  meetingOptions?: MeetingOption[];
  meetingDocumentLanguage?: Language[];
  duplicateProjectId?: string;
  ordinaryGeneralMeeting?: boolean;
  isGeneratedName?: boolean;
};

export type ProjectApi = {
  projectTemplateAttachmentUrl: (params: {
    orgNo: string;
    templateId: string;
    attachmentId: string;
  }) => string;

  projectTemplateDocumentPdfUrl: (params: {
    orgNo: string;
    templateId: string;
    documentId: string;
    recipientId?: string;
    removedDecisionIds?: string[];
    removedAttachmentIds?: string[];
  }) => string;

  downloadAttachmentUrl: (params: { projectId: string; attachmentId: string }) => string;

  downloadSealedDocumentUrl: (params: { projectId: string; documentId: string }) => string;

  dseVedtakDocumentUrl: (params: { projectId: string; navn: string }) => string;

  dseResponseXmlUrl: (params: { projectId: string; mottakId: string }) => string;

  pollProjectStatus: (params: {
    projectId: string;
    version: number;
    onSuccess: (res: HttpResponse<ProjectStatusResponse>) => DCEvent[];
    onFailure: () => DCEvent[];
  }) => DCEvent[];

  createProject: (
    payload: CreateProjectRequest,
    params: {
      onSuccess: (res: HttpResponse<CreateProjectResponse>) => DCEvent[];
      onFailure: (err: unknown) => DCEvent[];
    },
  ) => DCEvent;

  getProjects: (
    orgNo: string,
    params: {
      onSuccess: (res: HttpResponse<GetProjectListResponse>) => DCEvent[];
      onFailure: (err: unknown) => DCEvent[];
    },
  ) => DCEvent;

  getRecentMatters: (params: {
    onSuccess: (res: HttpResponse<RecentMattersResponse>) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) => DCEvent;

  getRecentMatterDetails: (
    projectId: string,
    decisionId: string,
    params: {
      onSuccess: (res: HttpResponse<RecentMatterDetailsResponse>) => DCEvent[];
      onFailure: (err: unknown) => DCEvent[];
    },
  ) => DCEvent;

  deleteProject: (
    projectId: string,
    params: {
      onSuccess: (res: HttpResponse<void>) => DCEvent[];
      onFailure: (err: unknown) => DCEvent[];
    },
  ) => DCEvent;

  starDecision: (
    payload: { projectId: string; decisionId: string; starred: boolean },
    params: {
      onSuccess: (res: HttpResponse<void>) => DCEvent[];
      onFailure: (err: unknown) => DCEvent[];
    },
  ) => DCEvent;

  createMeeting: (
    payload: CreateMeetingRequest,
    params: {
      onSuccess: (res: HttpResponse<CreateProjectResponse>) => DCEvent[];
      onFailure: () => DCEvent[];
    },
  ) => DCEvent;
};

const withQuery = (url: string, params: string[][]): string =>
  params.length === 0 ? url : `${url}?${new URLSearchParams(params).toString()}`;

export const projectApi: ProjectApi = {
  projectTemplateAttachmentUrl: ({ orgNo, templateId, attachmentId }) =>
    `/api/projects/${encodeURIComponent(orgNo)}/templates/${encodeURIComponent(templateId)}/attachments/${encodeURIComponent(attachmentId)}`,

  projectTemplateDocumentPdfUrl: ({
    orgNo,
    templateId,
    documentId,
    recipientId,
    removedDecisionIds = [],
    removedAttachmentIds = [],
  }) =>
    withQuery(
      `/api/projects/${encodeURIComponent(orgNo)}/templates/${encodeURIComponent(templateId)}/documents/${encodeURIComponent(documentId)}/pdf`,
      [
        ...(recipientId === undefined ? [] : [["recipientId", recipientId]]),
        ...removedDecisionIds.map((id) => ["removedDecisionIds", id]),
        ...removedAttachmentIds.map((id) => ["removedAttachmentIds", id]),
      ],
    ),

  downloadAttachmentUrl: ({ projectId, attachmentId }) =>
    `/api/project/${projectId}/attachment/${attachmentId}`,

  downloadSealedDocumentUrl: ({ projectId, documentId }) =>
    `/api/project/${projectId}/download-document/${documentId}`,

  dseVedtakDocumentUrl: ({ projectId, navn }) =>
    `/api/project/${projectId}/dse/incorporation/vedtak-document?navn=${encodeURIComponent(navn)}`,

  dseResponseXmlUrl: ({ projectId, mottakId }) =>
    `/api/project/${projectId}/dse/incorporation/response-xml?mottakId=${encodeURIComponent(mottakId)}`,

  pollProjectStatus: ({ projectId, version, onSuccess, onFailure }) => [
    httpGet({
      url: `/project/${projectId}/status?v=${version}`,
      onSuccess,
      onFailure,
    }),
  ],

  createProject: (payload, { onSuccess, onFailure }) =>
    httpPost({
      url: "/projects",
      data: payload,
      onSuccess,
      onFailure,
    }),

  getProjects: (orgNo, { onSuccess, onFailure }) =>
    httpGet({
      url: `/projects/${orgNo}`,
      onSuccess,
      onFailure,
    }),

  getRecentMatters: ({ onSuccess, onFailure }) =>
    httpGet({
      url: "/projects/recent-matters",
      onSuccess,
      onFailure,
    }),

  getRecentMatterDetails: (projectId: string, decisionId: string, { onSuccess, onFailure }) =>
    httpGet({
      url: `/project/${projectId}/recent-matter/${decisionId}`,
      onSuccess,
      onFailure,
    }),

  deleteProject: (projectId, { onSuccess, onFailure }) =>
    httpDelete({
      url: `/project/${projectId}`,
      onSuccess,
      onFailure,
    }),

  starDecision: ({ projectId, decisionId, starred }, { onSuccess, onFailure }) =>
    httpPost({
      url: `/project/${projectId}/star-decision`,
      data: { decisionId, starred },
      onSuccess,
      onFailure,
    }),

  createMeeting: (payload, { onSuccess, onFailure }) =>
    httpPost({
      url: `/projects/new-meeting`,
      data: payload,
      onSuccess,
      onFailure,
    }),
};
